import React, { useMemo, useState } from 'react';
import { shallowEqual, useSelector } from 'react-redux';
import NoResults from 'react/elements/NoResults';
import MealIdeaGroup from './MealIdeaGroup';
import NewMealIdeaGroupForm from './NewMealIdeaGroupForm';
import searchMealIdeas from 'react/components/calendar/searchMealIdeas';
import { selectMealIdeasIndex, selectMealIdeasLoaded, selectUngroupedIdeaIds } from '../../../redux/food/selectors';

/**
 * Every group, then the ideas that are in none. The Ungrouped section also shows when there are no
 * groups at all, since that is the only place a first idea can be added.
 *
 * `onOpenIdea`, when given, is called with an idea's id and row element when its row is clicked, in
 * place of selecting it.
 *
 * The search box narrows every group to the ideas whose name matches (the calendar picker's fuzzy
 * search), opens the groups that have any and hides the rest. While it is narrowed nothing can be
 * dropped into a group, since a drop's index would count only the visible rows; dragging an idea
 * onto the calendar still works.
 */
export default function MealIdeaGroupList({ onRequestDelete, onOpenIdea = null }) {
	const loaded = useSelector(selectMealIdeasLoaded);
	const groupOrder = useSelector((state) => selectMealIdeasIndex(state).groupOrder, shallowEqual);
	const ungrouped = useSelector((state) => selectUngroupedIdeaIds(selectMealIdeasIndex(state)), shallowEqual);
	const ideasById = useSelector((state) => selectMealIdeasIndex(state).ideasById);
	const [query, setQuery] = useState('');

	const trimmed = query.trim();
	const matchIds = useMemo(() => (trimmed === '' ? null :
		new Set(searchMealIdeas(Object.values(ideasById), trimmed).map((idea) => idea.id))), [ideasById, trimmed]);

	const nothingYet = loaded && !groupOrder.length && !ungrouped.length;
	const shownUngrouped = matchIds ? ungrouped.filter((id) => matchIds.has(id)) : ungrouped;

	return (
		<div className='meal_idea_group_list'>
			{!nothingYet &&
			<div className='meal_idea_search'>
				<i className='material-icons meal_idea_search__icon' aria-hidden='true'>search</i>
				<input
					type='search'
					className='meal_idea_search__input browser-default'
					placeholder='Search meal ideas'
					aria-label='Search meal ideas'
					value={query}
					onChange={(event) => setQuery(event.target.value)}
					onKeyDown={(event) => {
						if(event.key === 'Escape') {
							setQuery('');
						}
					}}
				/>
			</div>
			}
			{nothingYet && <NoResults>No meal ideas yet</NoResults>}
			{matchIds && matchIds.size === 0 && <NoResults>No meal ideas match &ldquo;{trimmed}&rdquo;</NoResults>}
			{groupOrder.map((groupId) => (
				<MealIdeaGroup key={groupId} groupId={groupId} matchIds={matchIds} onRequestDelete={onRequestDelete} onOpenIdea={onOpenIdea} />
			))}
			{(matchIds ? shownUngrouped.length > 0 : (ungrouped.length > 0 || !groupOrder.length)) &&
			<MealIdeaGroup groupId={null} ideaIds={shownUngrouped} matchIds={matchIds} onRequestDelete={onRequestDelete} onOpenIdea={onOpenIdea} />
			}
			{!matchIds && <NewMealIdeaGroupForm />}
		</div>
	);
}
